import { COLORS, FIELD, KICK, type Ball, type MatchState, type Possession, type TeamSide } from "@golazo/shared";
import type { KickKind } from "../game/kick.js";
import type { TacticsWorld } from "../ai/tactics.js";
import type { ScreenFx } from "../render/screenFx.js";
import type { StadiumBackdrop } from "../render/stadium.js";
import type { TrailsLayer } from "../render/trails.js";
import { createAudioJuice, type AudioJuice } from "./audio.js";
import { createCameraJuice, type CameraJuice } from "./camera.js";
import { createParticles, type Particles } from "./particles.js";
import { createShake, type Shake } from "./shake.js";

export interface VisualFxHooks {
  stadium?: StadiumBackdrop;
  screenFx?: ScreenFx;
  trails?: TrailsLayer;
}

export interface JuiceView {
  shake: Shake;
  camera: CameraJuice;
  particles: Particles;
  audio: AudioJuice;
  /** Lee el estado tras cada tick de simulacion y dispara efectos. Solo lectura. */
  observe(
    ball: Ball,
    possession: Possession,
    match: MatchState,
    tactics: TacticsWorld,
    dt: number,
  ): void;
  /** Feedback de dash del jugador humano. Solo render. */
  onDash(x: number, y: number, fx?: number, fy?: number, side?: TeamSide): void;
  /** Feedback de entrada defensiva. Solo render. */
  onTackle(x: number, y: number): void;
  /** Balon levantado (sombrero o malabar). Solo render. */
  onLift(x: number, y: number, scoop: boolean): void;
  /** Caño. Solo render. */
  onNutmeg(x: number, y: number, victimX: number, victimY: number): void;
  /** Poner el cuerpo: choque seco donde impacta, no donde estabas. */
  onBodyCheck(x: number, y: number, victimX: number, victimY: number): void;
  /** El cuerpo ha rebotado: el que llevaba el balon lo tenia plantado. */
  onBodyBlock(x: number, y: number, victimX: number, victimY: number): void;
  /** Armaste el remate y el balon nunca llego: pierna al aire. */
  onFirstTimeWhiff(x: number, y: number): void;
  /** Corte seco. Solo render. */
  onCut(x: number, y: number): void;
  /** Golpeo de primera: la sacudida y el destello escalan con el timing. */
  onFirstTime(x: number, y: number, quality: number): void;
  /** Pase o tiro desde SimHooks: refuerzo visual preciso. */
  onKick(kind: KickKind, x: number, y: number, side: TeamSide, power: number): void;
  /** Choque en banda: sacudida corta tipo "pum". */
  onSidelineBump(x: number, y: number, intensity: number): void;
  /** Balon contra el palo: sacudida del campo + metal. */
  onPostHit(x: number, y: number, speed: number): void;
  /** Actualiza animaciones visuales (particulas, shake, camara). */
  update(dt: number, match: MatchState): void;
  resize(screenWidth: number, screenHeight: number): void;
  dispose(): void;
}

interface Snapshot {
  phase: MatchState["phase"];
  ownerId: string | null;
  ballSpeed: number;
  ballX: number;
  ballY: number;
  ballVx: number;
  ballVy: number;
  nearGkSave: boolean;
  homeTeamPhase: TeamPhaseLike;
  awayTeamPhase: TeamPhaseLike;
}

type TeamPhaseLike = TacticsWorld["home"]["phase"];

function ballSpeed(ball: Ball): number {
  return Math.hypot(ball.vel.x, ball.vel.y);
}

export function createJuice(fx: VisualFxHooks = {}): JuiceView {
  const shake = createShake();
  const camera = createCameraJuice();
  const particles = createParticles();
  const audio = createAudioJuice();
  const { stadium, screenFx, trails } = fx;

  let prev: Snapshot = {
    phase: "playing",
    ownerId: null,
    ballSpeed: 0,
    ballX: 0,
    ballY: 0,
    ballVx: 0,
    ballVy: 0,
    nearGkSave: false,
    homeTeamPhase: "loose",
    awayTeamPhase: "loose",
  };
  /** Evita acumular trauma si el balon roza el palo varios frames. */
  let postHitCooldown = 0;

  const teamColor = (side: TeamSide): number => (side === "home" ? 0xff8a8e : 0x8fa8ff);

  return {
    shake,
    camera,
    particles,
    audio,

    observe(ball, possession, match, tactics, _dt): void {
      const speed = ballSpeed(ball);
      const ownerId = possession.ownerId;

      for (const side of ["home", "away"] as const) {
        const team = tactics[side];
        const prevPhase = side === "home" ? prev.homeTeamPhase : prev.awayTeamPhase;
        if (team.phase === "transition" && prevPhase !== "transition") {
          shake.add(0.035);
          particles.burst(ball.pos.x, ball.pos.y, 6, {
            speed: 140,
            life: 0.25,
            size: 3,
            spread: Math.PI * 2,
            color: teamColor(side),
          });
        }
      }

      if (ownerId !== null && ownerId !== prev.ownerId) {
        particles.ring(ball.pos.x, ball.pos.y, 0xf2fff5, 22);
      }

      if (match.phase === "goal" && prev.phase !== "goal") {
        shake.add(0.85);
        camera.focusGoal(ball.pos.x, ball.pos.y);
        particles.confetti(ball.pos.x, ball.pos.y, teamColor(match.lastScorer ?? "home"));
        audio.playGoal();
        stadium?.cheer(1);
        screenFx?.pulse(0.95, teamColor(match.lastScorer ?? "home"));
      }

      if (match.phase === "playing") {
        audio.ambient.update(_dt, {
          ballSpeed: speed,
          ballX: ball.pos.x,
          ballLoft: ball.loft,
          phase: match.phase,
        });

        const inAttThird = ball.pos.x > FIELD.WIDTH * 0.68 || ball.pos.x < FIELD.WIDTH * 0.32;
        if (inAttThird && speed > 620 && ownerId === null) {
          const swell = Math.min(1, speed / 1400);
          audio.ambient.onAttackSwell(swell);
          stadium?.swell(swell * 0.65);
        }
      }

      if (prev.ownerId !== null && ownerId === null && speed > 380) {
        const isShoot = speed >= KICK.SHOOT_SPEED * KICK.MIN_POWER * 0.85;
        if (isShoot) {
          shake.add(0.06 + Math.min(0.1, speed / KICK.SHOOT_SPEED * 0.1));
        } else {
          shake.add(0.08);
          audio.playPass(speed);
          particles.burst(ball.pos.x, ball.pos.y, 8, {
            speed: 180 + speed * 0.06,
            size: 3,
            spread: Math.PI * 0.5,
          });
        }
      }

      const nearWall =
        ball.pos.x <= ball.radius + 2 ||
        ball.pos.x >= FIELD.WIDTH - ball.radius - 2 ||
        ball.pos.y <= ball.radius + 2 ||
        ball.pos.y >= FIELD.HEIGHT - ball.radius - 2;

      if (nearWall && speed > 260) {
        const reversedX = Math.sign(ball.vel.x) !== Math.sign(prev.ballVx) && Math.abs(prev.ballVx) > 80;
        const reversedY = Math.sign(ball.vel.y) !== Math.sign(prev.ballVy) && Math.abs(prev.ballVy) > 80;
        if (reversedX || reversedY) {
          shake.add(0.06 + Math.min(0.1, speed / 2400));
          audio.playWall();
          particles.burst(ball.pos.x, ball.pos.y, 5, {
            speed: 120,
            life: 0.2,
            size: 3,
            color: 0xf2fff5,
          });
        }
      }

      const wasThreat = prev.ballSpeed > 400 && Math.abs(prev.ballVx) > 200;
      const nearGoal = ball.pos.x < FIELD.WIDTH * 0.2 || ball.pos.x > FIELD.WIDTH * 0.8;
      const speedDrop = prev.ballSpeed - speed > 280;
      if (wasThreat && nearGoal && speedDrop && !prev.nearGkSave) {
        shake.add(0.12);
        audio.playSave();
        audio.ambient.onGasp(Math.min(1, prev.ballSpeed / 1200));
        particles.burst(ball.pos.x, ball.pos.y, 8, {
          speed: 160,
          life: 0.22,
          size: 3,
          color: 0xf2fff5,
          spread: Math.PI * 2,
        });
      }

      prev = {
        phase: match.phase,
        ownerId,
        ballSpeed: speed,
        ballX: ball.pos.x,
        ballY: ball.pos.y,
        ballVx: ball.vel.x,
        ballVy: ball.vel.y,
        nearGkSave: wasThreat && nearGoal && speedDrop,
        homeTeamPhase: tactics.home.phase,
        awayTeamPhase: tactics.away.phase,
      };
    },

    onDash(x, y, fx = 0, fy = 1, side = "home"): void {
      shake.add(0.07);
      audio.playDash();
      particles.burst(x, y, 12, {
        speed: 360,
        size: 4,
        spread: Math.PI * 0.45,
        color: COLORS.CONTROL_RING,
        life: 0.25,
      });
      trails?.burstDash(x, y, fx, fy, teamColor(side));
    },

    onLift(x: number, y: number, scoop: boolean): void {
      audio.playLift(scoop);
      if (scoop) {
        shake.add(0.05);
        particles.burst(x, y, 8, {
          speed: 200,
          size: 3,
          spread: Math.PI * 0.5,
          color: COLORS.AIM,
          life: 0.3,
        });
      } else {
        particles.burst(x, y, 4, {
          speed: 110,
          size: 2,
          spread: Math.PI * 2,
          color: COLORS.AIM,
          life: 0.22,
        });
      }
    },

    onFirstTime(x: number, y: number, quality: number): void {
      const q = Math.max(0, Math.min(1, quality));
      audio.playFirstTime(q);
      shake.add(0.04 + q * 0.12);
      particles.burst(x, y, 6 + Math.round(q * 14), {
        speed: 220 + q * 320,
        size: 3 + q * 2,
        spread: Math.PI * (0.55 - q * 0.35),
        color: q > 0.8 ? COLORS.AIM : COLORS.CONTROL_RING,
        life: 0.24 + q * 0.2,
      });
      if (q > 0.75) screenFx?.pulse(0.35 + q * 0.35, COLORS.AIM);
    },

    onNutmeg(x: number, y: number, victimX: number, victimY: number): void {
      audio.playNutmeg();
      shake.add(0.08);
      particles.burst(x, y, 10, {
        speed: 280,
        size: 3.5,
        spread: Math.PI * 0.55,
        color: COLORS.CONTROL_RING,
        life: 0.28,
      });
      particles.burst(victimX, victimY, 6, {
        speed: 160,
        size: 2.5,
        spread: Math.PI * 2,
        color: COLORS.AIM,
        life: 0.22,
      });
    },

    onBodyBlock(x: number, y: number, victimX: number, victimY: number): void {
      /*
       * Mas gordo que un cuerpo normal, y a proposito.
       *
       * Bloquear es acertar una ventana de dos decimas contra alguien que venia
       * lanzado; si sonara igual que cualquier choque, el jugador no sabria que
       * ha hecho algo. La sacudida y el destello SON la explicacion de la
       * mecanica: nadie va a leer un tutorial para enterarse.
       */
      shake.add(0.26);
      audio.playTackle();
      const contactX = (x + victimX) * 0.5;
      const contactY = (y + victimY) * 0.5;
      particles.burst(contactX, contactY, 20, {
        speed: 460,
        size: 5.5,
        spread: Math.PI * 2,
        color: COLORS.CONTROL_RING,
        life: 0.32,
      });
    },

    onBodyCheck(x: number, y: number, victimX: number, victimY: number): void {
      // La sacudida es mas fuerte que la entrada (0,1) porque esto es un
      // impacto de cuerpo, y las particulas salen en el punto de CONTACTO, no
      // en el del que embiste: es donde el ojo esta mirando.
      shake.add(0.16);
      audio.playTackle();
      const contactX = (x + victimX) * 0.5;
      const contactY = (y + victimY) * 0.5;
      particles.burst(contactX, contactY, 12, {
        speed: 320,
        size: 4.5,
        spread: Math.PI * 2,
        color: COLORS.HUD_TEXT,
        life: 0.24,
      });
    },

    onFirstTimeWhiff(x: number, y: number): void {
      // Callado a proposito: fallar ya se castiga solo con la ocasion perdida,
      // y un ruido fuerte cada vez que te anticipas convertiria el gesto en
      // algo que no apetece intentar. Un soplo de cesped y nada mas.
      particles.burst(x, y, 5, {
        speed: 130,
        size: 2.5,
        spread: Math.PI * 0.9,
        color: COLORS.HUD_TEXT,
        life: 0.18,
      });
    },

    onCut(x: number, y: number): void {
      audio.playCut();
      shake.add(0.045);
      particles.burst(x, y, 7, {
        speed: 240,
        size: 3,
        spread: Math.PI * 0.7,
        color: COLORS.CONTROL_RING,
        life: 0.22,
      });
    },

    onTackle(x: number, y: number): void {
      shake.add(0.1);
      audio.playTackle();
      particles.burst(x, y, 10, {
        speed: 280,
        size: 5,
        spread: Math.PI * 0.35,
        color: COLORS.HUD_TEXT,
        life: 0.2,
      });
    },

    onKick(kind, x, y, side, power): void {
      const color = teamColor(side);
      const curved = Math.min(1, power);
      if (kind === "shoot") {
        const perfect = curved >= KICK.PERFECT_CHARGE;
        shake.add(0.18 + curved * 0.32);
        camera.punchShot(curved);
        audio.playKick(KICK.SHOOT_SPEED * (KICK.MIN_POWER + curved * (1 - KICK.MIN_POWER)));
        audio.ambient.onAttackSwell(0.45 + curved * 0.45);
        particles.ring(x, y, perfect ? 0xffe58a : color, 28 + curved * 22);
        particles.burst(x, y, perfect ? 22 : 14 + Math.round(curved * 8), {
          speed: 240 + curved * 420,
          life: 0.28 + curved * 0.12,
          size: 3 + curved * 3,
          spread: Math.PI * 0.55,
          color: perfect ? 0xffe58a : color,
        });
        if (perfect) {
          particles.ring(x, y, 0xf2fff5, 44);
          screenFx?.pulse(0.72, 0xffe58a);
        } else {
          screenFx?.pulse(0.18 + curved * 0.28, color);
        }
      } else if (kind === "pass") {
        particles.ring(x, y, color, 32);
        particles.burst(x, y, 4, {
          speed: 90,
          life: 0.18,
          size: 2,
          color,
          spread: Math.PI * 2,
        });
      } else if (kind === "cross") {
        particles.ring(x, y, color, 38);
      }
    },

    onSidelineBump(x, y, intensity): void {
      shake.add(0.05 + intensity * 0.11);
      audio.playWall();
      particles.burst(x, y, 6, {
        speed: 70 + intensity * 90,
        life: 0.16,
        size: 2.5,
        color: 0xf2fff5,
        spread: Math.PI * 0.55,
      });
      particles.ring(x, y, 0xd8e8dc, 18 + intensity * 14);
    },

    onPostHit(x, y, speed): void {
      if (postHitCooldown > 0) return;
      postHitCooldown = 0.28;
      const intensity = Math.min(1, speed / 1400);
      shake.add(0.035 + intensity * 0.055);
      audio.playPost(speed);
      audio.ambient.onGasp(0.4 + intensity * 0.3);
      particles.burst(x, y, 6 + Math.round(intensity * 4), {
        speed: 100 + intensity * 120,
        life: 0.18,
        size: 2.5,
        color: COLORS.GOAL_FRAME,
        spread: Math.PI * 0.6,
      });
      particles.ring(x, y, 0xfff6d6, 16 + intensity * 12);
    },

    update(dt, match): void {
      postHitCooldown = Math.max(0, postHitCooldown - dt);
      shake.update(dt);
      camera.update(dt, match);
      particles.update(dt);
      stadium?.update(dt);
      screenFx?.update(dt);
      if (match.phase !== "playing") {
        audio.ambient.update(dt, {
          ballSpeed: 0,
          ballX: FIELD.WIDTH / 2,
          ballLoft: 0,
          phase: match.phase,
        });
      }
    },

    resize(screenWidth, screenHeight): void {
      camera.resize();
      stadium?.layout(screenWidth, screenHeight);
      screenFx?.layout(screenWidth, screenHeight);
    },

    dispose(): void {
      audio.dispose();
    },
  };
}
